import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faShieldHalved,
	faCode,
	faCloud,
	faFlask,
	faRocket,
	faFeatherPointed,
	faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import SocialLinks from "../components/ui/SocialLinks";
import GlassCard from "../components/ui/GlassCard";
import { usePortfolioData } from "../contexts/DataContext";
import "./styles/HeroAbout.css";

const iconMap = { faShieldHalved, faCode, faCloud, faFlask, faRocket };

const letterVariants = {
	hidden: { opacity: 0, y: 30, filter: "blur(4px)" },
	visible: (i) => ({
		opacity: 1,
		y: 0,
		filter: "blur(0px)",
		transition: { delay: i * 0.04, duration: 0.5, ease: "easeOut" },
	}),
};

const fadeUp = {
	hidden: { opacity: 0, y: 30 },
	visible: (delay = 0) => ({
		opacity: 1,
		y: 0,
		transition: { delay, duration: 0.7, ease: "easeOut" },
	}),
};

const staggerContainer = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

const staggerItem = {
	hidden: { opacity: 0, y: 25 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const HeroAbout = () => {
	const { data: INFO } = usePortfolioData();
	const nameChars = INFO.main.name.split("");
	const featuredProject = INFO.projects.find((p) => p.highlight);
	const featuredResearch = INFO.research?.[0];

	return (
		<section id="hero" className="hero-section">
			<div className="hero-orb hero-orb--1" />
			<div className="hero-orb hero-orb--2" />
			<div className="hero-orb hero-orb--3" />
			<div className="hero-grid-bg" />

			{/* ---- Hero ---- */}
			<div className="hero-content content-wrapper">
				<div className="hero-main">
					<motion.p
						className="hero-greeting"
						variants={fadeUp}
						initial="hidden"
						animate="visible"
						custom={0}
					>
						Hey, I'm
					</motion.p>

					<h1 className="hero-name">
						{nameChars.map((char, i) => (
							<motion.span
								key={i}
								variants={letterVariants}
								initial="hidden"
								animate="visible"
								custom={i}
								className={char === " " ? "hero-name-space" : ""}
							>
								{char === " " ? "\u00A0" : char}
							</motion.span>
						))}
					</h1>

					<motion.p
						className="hero-role"
						variants={fadeUp}
						initial="hidden"
						animate="visible"
						custom={0.6}
					>
						{INFO.main.role}
					</motion.p>

					<motion.p
						className="hero-tagline"
						variants={fadeUp}
						initial="hidden"
						animate="visible"
						custom={0.8}
					>
						{INFO.main.tagline}
					</motion.p>

					<motion.div
						className="hero-cta-row"
						variants={fadeUp}
						initial="hidden"
						animate="visible"
						custom={1.0}
					>
						<Link to="/contact" className="hero-cta-btn">
							Say hello
						</Link>
						<Link to="/projects" className="hero-cta-btn hero-cta-btn--ghost">
							See what I'm building
						</Link>
					</motion.div>

				</div>

				<motion.div
					className="hero-scroll-indicator"
					animate={{ y: [0, 10, 0] }}
					transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
				>
					<div className="hero-scroll-line" />
				</motion.div>
			</div>

			{/* ---- About ---- */}
			<div className="about-section content-wrapper">
				<motion.div
					initial={{ opacity: 0, y: 50 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.2 }}
					transition={{ duration: 0.8, ease: "easeOut" }}
				>
					<GlassCard className="about-card" hover={false}>
						<div className="about-card__inner">
							<div className="about-card__text">
								<h2 className="about-card__title">
									{INFO.about.title}
								</h2>
								<p className="about-card__description">
									{INFO.about.description}
								</p>
							</div>
						</div>
					</GlassCard>
				</motion.div>
			</div>

			{/* ---- What I Do ---- */}
			<div className="whatido-section content-wrapper">
				<motion.div
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.15 }}
					transition={{ duration: 0.7 }}
				>
					<h2 className="whatido-heading">
						What I actually do all day
					</h2>
				</motion.div>

				<motion.div
					className="whatido-grid"
					variants={staggerContainer}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.1 }}
				>
					{INFO.about.whatIDo.map((item, i) => (
						<motion.div key={i} variants={staggerItem} className="whatido-item">
							<div className="whatido-item__icon">
								<FontAwesomeIcon icon={iconMap[item.icon] || faCode} />
							</div>
							<div>
								<h3 className="whatido-item__title">{item.title}</h3>
								<p className="whatido-item__desc">{item.description}</p>
							</div>
						</motion.div>
					))}
				</motion.div>
			</div>

			{/* ---- Currently building ---- */}
			{featuredProject && (
				<div className="glimpse content-wrapper">
					<motion.div
						initial={{ opacity: 0, y: 40 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.15 }}
						transition={{ duration: 0.7 }}
					>
						<div className="glimpse__row">
							<div className="glimpse__text">
								<span className="glimpse__label">Currently building</span>
								<h3 className="glimpse__title">{featuredProject.title}</h3>
								<p className="glimpse__desc">
									{featuredProject.description.substring(0, 180)}...
								</p>
								<div className="glimpse__tags">
									{featuredProject.tags.slice(0, 4).map((tag) => (
										<span key={tag} className="glimpse__tag">{tag}</span>
									))}
								</div>
								<Link to="/projects" className="glimpse__link">
									More about my projects <FontAwesomeIcon icon={faArrowRight} />
								</Link>
							</div>
							</div>
					</motion.div>
				</div>
			)}

			{/* ---- Research ---- */}
			{featuredResearch && (
				<div className="glimpse content-wrapper">
					<motion.div
						initial={{ opacity: 0, y: 40 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.15 }}
						transition={{ duration: 0.7 }}
					>
						<div className="glimpse__row">
							<div className="glimpse__text">
								<span className="glimpse__label">Research I'm working on</span>
								<h3 className="glimpse__title">{featuredResearch.title}</h3>
								<p className="glimpse__desc">
									{featuredResearch.description.substring(0, 200)}...
								</p>
								<Link to="/research" className="glimpse__link">
									Read more <FontAwesomeIcon icon={faArrowRight} />
								</Link>
							</div>
						</div>
					</motion.div>
				</div>
			)}

			{/* ---- Work ---- */}
			{INFO.experience?.[0] && (
				<div className="glimpse content-wrapper">
					<motion.div
						initial={{ opacity: 0, y: 40 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.15 }}
						transition={{ duration: 0.7 }}
					>
						<div className="glimpse__row">
							<div className="glimpse__text">
								<span className="glimpse__label">Where I work</span>
								<h3 className="glimpse__title">{INFO.experience[0].role}</h3>
								<p className="glimpse__company">{INFO.experience[0].company}</p>
								<p className="glimpse__desc">
									{INFO.experience[0].description.substring(0, 160)}...
								</p>
								<Link to="/experience" className="glimpse__link">
									More about what I do there <FontAwesomeIcon icon={faArrowRight} />
								</Link>
							</div>
						</div>
					</motion.div>
				</div>
			)}

			{/* ---- Other stuff ---- */}
			<div className="glimpse content-wrapper">
				<motion.div
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.15 }}
					transition={{ duration: 0.7 }}
				>
					<div className="glimpse__row">
						<div className="glimpse__text">
							<span className="glimpse__label">Other stuff I care about</span>
							<div className="glimpse__interests-preview">
								{INFO.interests.map((item, i) => (
									<span key={i} className="glimpse__interest-item">
										{item.title}
									</span>
								))}
							</div>
							<Link to="/interests" className="glimpse__link">
								More about my interests <FontAwesomeIcon icon={faArrowRight} />
							</Link>
						</div>
					</div>
				</motion.div>
			</div>

			{/* ---- Poetry ---- */}
			{INFO.poetry && INFO.poetry.length > 0 && (
				<div className="glimpse glimpse--centered content-wrapper">
					<motion.div
						initial={{ opacity: 0, y: 40 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.2 }}
						transition={{ duration: 0.7 }}
					>
						<FontAwesomeIcon icon={faFeatherPointed} className="glimpse__center-icon" />
						<span className="glimpse__label glimpse__label--center">One more thing</span>
						<h3 className="glimpse__title glimpse__title--center">I write poetry too.</h3>
						<p className="glimpse__desc glimpse__desc--center">
							Sometimes the best way to process things is to write about them.
						</p>
						<a href="https://www.instagram.com/poetrypin__ts/" target="_blank" rel="noopener noreferrer" className="glimpse__link glimpse__link--center">
							Read some <FontAwesomeIcon icon={faArrowRight} />
						</a>
					</motion.div>
				</div>
			)}
		</section>
	);
};

export default HeroAbout;
